import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import style from "./styles";
import PremierSubscriptionOption from "../PremierSubscriptionOption/index.jsx";
import {
  getSubscriptionMessage,
  hasRecurringTerms,
  supportsAutoRenewal,
} from "../../utils/subscriptionMessages";
import getPremierAutoRenewalUrl from "../../utils/getPremierAutoRenewalUrl";
import trackAddToBag from "../../utils/trackAddToBag";
import { useLocaleProvider, useTranslate } from "../../context/locale-provider";
import StripeLogo from "../../assets/stripe-logo.svg";

// Add the subscription to the bag, then reset the joining state regardless of
// outcome. Errors are already handled/dispatched inside addPremierSubscription,
// so we only need to swallow any residual rejection to avoid an unhandled one.
const addToBagAndReset = (addSubscriptionPromise, setIsJoining) => {
  Promise.resolve(addSubscriptionPromise)
    .catch(() => {})
    .finally(() => setIsJoining(false));
};

// Render the limited markup the API uses in messages: `\n` as line breaks and
// `<b>…</b>` as bold, without touching innerHTML.
const renderApiMessage = (text) =>
  text.split(/(<b>.*?<\/b>)/s).map((part, index) => {
    const bold = part.match(/^<b>(.*)<\/b>$/s);
    const lines = (bold ? bold[1] : part)
      .split("\n")
      .flatMap((line, lineIndex) =>
        lineIndex === 0 ? [line] : [<br key={`${index}-${lineIndex}`} />, line]
      );
    return bold ? <strong key={index}>{lines}</strong> : lines;
  });

const PremierSubscriptionPlans = ({
  highReturner,
  checkingEligibility,
  status,
  plans,
  selectedCountry,
  premierSubscriptionAdded,
  fetchSubscriptionPlans,
  addPremierSubscription,
  openTermsAndConditions,
}) => {
  const translate = useTranslate(useLocaleProvider());
  const context =
    typeof window !== "undefined" && window.asos && window.asos.webContext;
  const currency = context ? context.getBrowseCurrency() : "";
  const language = context ? context.getBrowseLanguage() : "";

  const [selectedId, setSelectedId] = useState(undefined);
  const [isJoining, setIsJoining] = useState(false);

  // Back from the purchase redirect restores the page from bfcache with isJoining still true.
  useEffect(() => {
    const onPageShow = (event) => {
      if (event.persisted) {
        setIsJoining(false);
      }
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  useEffect(() => {
    if (selectedCountry) {
      fetchSubscriptionPlans(selectedCountry, currency, language);
    }
  }, [selectedCountry]);

  const sortedPlans = useMemo(
    () => [...plans].sort((a, b) => a.period - b.period),
    [plans]
  );

  useEffect(() => {
    setSelectedId(sortedPlans[0] && sortedPlans[0].id);
  }, [sortedPlans]);

  if (status === "loading") {
    return (
      <section id="plans" className={style.PremierSubscriptionPlans}>
        <div className={style.inner}>
          <p className={style.status} aria-busy="true">
            {translate("premier_landingpage_plans_loading")}
          </p>
        </div>
      </section>
    );
  }

  if (status === "error" || sortedPlans.length === 0) {
    return (
      <section id="plans" className={style.PremierSubscriptionPlans}>
        <div className={style.inner}>
          <p className={style.status} role="alert">
            {translate("premier_landingpage_plans_error")}
          </p>
        </div>
      </section>
    );
  }

  const selectedOption =
    sortedPlans.find((option) => option.id === selectedId) || sortedPlans[0];
  const isOnlyOption = sortedPlans.length === 1;
  const recurringStatement = getSubscriptionMessage(
    selectedOption,
    "RecurringInstructionStatement"
  );

  const handleJoin = () => {
    if (
      isJoining ||
      premierSubscriptionAdded ||
      highReturner ||
      checkingEligibility
    ) {
      return;
    }
    if (hasRecurringTerms(selectedOption)) {
      setIsJoining(true);
      trackAddToBag(selectedOption.price.text, !!selectedOption.trialOffer);
      window.location.href = getPremierAutoRenewalUrl(
        selectedOption.id,
        !!selectedOption.trialOffer,
        selectedOption.trialOffer && selectedOption.trialOffer.id
      );
    } else {
      setIsJoining(true);
      trackAddToBag(selectedOption.price.text);
      addToBagAndReset(addPremierSubscription(selectedOption.id), setIsJoining);
    }
  };

  const renderTermsMessage = (text) =>
    text.split(/(<u>.*?<\/u>)/s).map((part, index) => {
      const underlined = part.match(/^<u>(.*)<\/u>$/s);
      return underlined ? (
        <button
          key={index}
          type="button"
          className={style.termsLink}
          onClick={() =>
            openTermsAndConditions(selectedOption.termsAndConditionsUrl)
          }
        >
          {underlined[1]}
        </button>
      ) : (
        part
      );
    });

  const ctaLabel = premierSubscriptionAdded
    ? translate("premier_landingpage_plans_added")
    : hasRecurringTerms(selectedOption)
    ? translate("premier_landingpage_plans_join_cta")
    : translate("premier_landingpage_plans_add_to_bag");

  return (
    <section id="plans" className={style.PremierSubscriptionPlans}>
      <div className={style.inner}>
        <div className={style.heading}>
          <h2 className={style.title}>
            {translate("premier_landingpage_plans_title")}
          </h2>
          <p className={style.subtitle}>
            {isOnlyOption
              ? translate("premier_landingpage_plans_subtitle_single")
              : translate("premier_landingpage_plans_subtitle_multi")}
          </p>
        </div>

        <h3 className={style.selectHeading} id="subscription-plans-heading">
          {isOnlyOption
            ? translate("premier_landingpage_plans_your_plan")
            : translate("premier_landingpage_plans_select_your_plan")}
        </h3>

        <div
          className={`${style.options} ${
            isOnlyOption ? style.optionsSingle : style.optionsMulti
          }`}
          role="radiogroup"
          aria-labelledby="subscription-plans-heading"
        >
          {sortedPlans.map((option, index) => (
            <PremierSubscriptionOption
              key={option.id}
              option={option}
              isPrimary={index === 0}
              isSelected={option.id === selectedOption.id}
              isDisabled={isJoining || premierSubscriptionAdded}
              isOnlyOption={isOnlyOption}
              onSelect={setSelectedId}
              currency={currency}
              language={language}
              freeLabel={translate("premier_landingpage_plans_price_free")}
            />
          ))}
        </div>

        <button
          type="button"
          className={style.joinCta}
          disabled={
            isJoining ||
            premierSubscriptionAdded ||
            highReturner ||
            checkingEligibility
          }
          onClick={handleJoin}
        >
          {ctaLabel}
        </button>

        {supportsAutoRenewal(selectedOption) && (
          <p className={style.stripeMessage}>
            {translate("premier_landingpage_plans_powered_by_stripe")}{" "}
            <StripeLogo className={style.stripeLogo} aria-label="Stripe" />
          </p>
        )}

        <p className={style.minimumSpend}>
          {translate("premier_landingpage_plans_minimum_spend")}{" "}
          <button
            type="button"
            className={style.termsLink}
            onClick={() =>
              openTermsAndConditions(selectedOption.termsAndConditionsUrl)
            }
          >
            {translate("premier_landingpage_plans_learn_more")}
          </button>
        </p>

        <div className={style.terms}>
          {hasRecurringTerms(selectedOption) && recurringStatement && (
            <p>{renderApiMessage(recurringStatement)}</p>
          )}
          <p>
            {renderTermsMessage(translate("premier_landingpage_plans_terms"))}
          </p>
        </div>
      </div>
    </section>
  );
};

PremierSubscriptionPlans.propTypes = {
  highReturner: PropTypes.bool,
  checkingEligibility: PropTypes.bool,
  status: PropTypes.oneOf(["loading", "success", "error"]).isRequired,
  plans: PropTypes.array.isRequired,
  selectedCountry: PropTypes.string,
  premierSubscriptionAdded: PropTypes.bool,
  fetchSubscriptionPlans: PropTypes.func.isRequired,
  addPremierSubscription: PropTypes.func.isRequired,
  openTermsAndConditions: PropTypes.func.isRequired,
};

export default PremierSubscriptionPlans;
